// React error boundary — catches render-phase errors in any descendant
// and:
//   1. Shows a friendly fallback ("Something went wrong, refresh to try
//      again") instead of a white screen
//   2. POSTs a crash report to /api/log/client so the server journal
//      captures what broke, on which page, with which stack
//
// React requires class-based components for componentDidCatch; no
// functional-component equivalent exists yet. This is the only class
// component in the app.
import React from "react";
import { T, T_NEW } from "../theme.js";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // Fire-and-forget; we never await this. If the server is unreachable,
    // we still want to render the fallback UI.
    try {
      const body = JSON.stringify({
        message: error?.message || String(error),
        stack: error?.stack || "",
        componentStack: info?.componentStack || "",
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
      fetch("/api/log/client", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,   // survives a tab-close, similar to sendBeacon
      }).catch(() => {});
    } catch { /* never throw inside a boundary */ }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", background: T_NEW.paper, color: T_NEW.ink,
                      display: "flex", flexDirection: "column", alignItems: "center",
                      justifyContent: "center", padding: 28, textAlign: "center",
                      fontFamily: T_NEW.fontSans }}>
          {/* Editorial envelope mark — torn corner suggests "letter
              returned to sender." Visual continuity with the empty
              states across the app. */}
          <svg width="96" height="72" viewBox="0 0 64 48" aria-hidden style={{ marginBottom: 22 }}>
            <rect x="2" y="6" width="60" height="36" rx="2" fill="none"
                  stroke={T_NEW.ink} strokeWidth="1.5"/>
            <path d="M2 8 L32 28 L62 8" fill="none" stroke={T_NEW.ink} strokeWidth="1.5"/>
            {/* Tear / wax-seal-broken accent */}
            <path d="M48 32 L56 40 M56 32 L48 40" stroke={T_NEW.danger} strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <h1 style={{ fontFamily: T_NEW.fontSerif, fontSize: T_NEW.hero,
                       fontWeight: 500, margin: "0 0 12px", color: T_NEW.ink, lineHeight: 1.1 }}>
            Что-то сломалось
          </h1>
          <p style={{ color: T_NEW.ink56, fontSize: T_NEW.bodySm, maxWidth: 440,
                      lineHeight: 1.55, marginBottom: 26, fontFamily: T_NEW.fontSerif,
                      fontStyle: "italic" }}>
            Страница неожиданно закрылась. Перезагрузите — мы уже записали инцидент в журнал.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ background: T_NEW.persimmon, color: T_NEW.paperOnInk, border: "none",
                     padding: "12px 28px", borderRadius: T_NEW.r1, fontSize: T_NEW.micro,
                     fontWeight: 700, cursor: "pointer", fontFamily: T_NEW.fontMono,
                     textTransform: "uppercase", letterSpacing: T_NEW.monoTrack }}>
            Перезагрузить
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
